import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/AcquistoMaterialeForm.css";

const API_URL = process.env.REACT_APP_API_URL;

const metodoPagamentoOptions = [
  { value: "CONTANTI", label: "Contanti" },
  { value: "BONIFICO", label: "Bonifico" },
  { value: "POS", label: "POS" },
  { value: "ALTRO", label: "Altro" }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 14,
    background: "rgba(255,255,255,0.12)",
    borderColor: state.isFocused ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    backdropFilter: "blur(6px)"
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: "hidden",
    background: "rgba(18, 31, 70, 0.96)",
    backdropFilter: "blur(10px)",
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff"
  }),
  input: (base) => ({
    ...base,
    color: "#fff"
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.70)"
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? "rgba(255,255,255,0.14)" : "transparent",
    color: "#fff",
    cursor: "pointer"
  })
};

const selectPortalProps = {
  menuPortalTarget: document.body,
  menuPosition: "fixed"
};

const formatEuro = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const getGiocatoreLabel = (g) => {
  const cognome = g?.cognome || "";
  const nome = g?.nome || "";
  const categoria = g?.categoriaNome || g?.categoria || "";
  return `${cognome} ${nome}${categoria ? ` - ${categoria}` : ""}`.trim();
};

const getStagioneLabel = (s) => {
  return s?.nome || s?.descrizione || s?.label || `Stagione ${s?.id ?? ""}`.trim();
};

const getArticoloLabel = (a) => {
  const parts = [a?.nome];
  if (a?.taglia) parts.push(`Taglia ${a.taglia}`);
  if (a?.quantitaDisponibile !== undefined && a?.quantitaDisponibile !== null) {
    parts.push(`Disp. ${a.quantitaDisponibile}`);
  }
  return parts.filter(Boolean).join(" - ");
};

const buildGiocatoreOptions = (items) =>
  (Array.isArray(items) ? items : []).map((g) => ({
    value: g.id,
    label: getGiocatoreLabel(g),
    raw: g
  }));

const buildStagioneOptions = (items) =>
  (Array.isArray(items) ? items : []).map((s) => ({
    value: s.id,
    label: getStagioneLabel(s),
    raw: s
  }));

const buildArticoloOptions = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((a) => a.stato === "ATTIVO")
    .map((a) => ({
      value: a.id,
      label: getArticoloLabel(a),
      raw: a
    }));

const createEmptyRiga = () => ({
  key: `${Date.now()}-${Math.random()}`,
  articoloOption: null,
  quantita: 1,
  prezzoUnitario: "",
  totaleRiga: 0
});

export default function AcquistoMaterialeForm() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [giocatori, setGiocatori] = useState([]);
  const [stagioni, setStagioni] = useState([]);
  const [articoli, setArticoli] = useState([]);

  const [loadingLookup, setLoadingLookup] = useState(true);
  const [saving, setSaving] = useState(false);

  const [giocatoreOption, setGiocatoreOption] = useState(null);
  const [stagioneOption, setStagioneOption] = useState(null);
  const [metodoPagamentoOption, setMetodoPagamentoOption] = useState(null);

  const [importoPagatoIniziale, setImportoPagatoIniziale] = useState("");
  const [note, setNote] = useState("");
  const [righe, setRighe] = useState([createEmptyRiga()]);

  const fetchLookup = useCallback(async () => {
    setLoadingLookup(true);
    try {
      const [resGiocatori, resStagioni, resArticoli] = await Promise.all([
        authFetch(`${API_URL}/api/admin/giocatori`),
        authFetch(`${API_URL}/api/admin/lookup/stagioni`),
        authFetch(`${API_URL}/api/admin/magazzino/articoli/attivi`)
      ]);

      if (!resGiocatori.ok) throw new Error("Errore nel caricamento giocatori");
      if (!resStagioni.ok) throw new Error("Errore nel caricamento stagioni");
      if (!resArticoli.ok) throw new Error("Errore nel caricamento articoli magazzino");

      const dataGiocatori = await resGiocatori.json().catch(() => []);
      const dataStagioni = await resStagioni.json().catch(() => []);
      const dataArticoli = await resArticoli.json().catch(() => []);

      const listaGiocatori = Array.isArray(dataGiocatori?.content) ? dataGiocatori.content : dataGiocatori;
      const listaStagioni = Array.isArray(dataStagioni) ? dataStagioni : [];
      const listaArticoli = Array.isArray(dataArticoli) ? dataArticoli : [];

      setGiocatori(buildGiocatoreOptions(listaGiocatori));
      setStagioni(buildStagioneOptions(listaStagioni));
      setArticoli(buildArticoloOptions(listaArticoli));
    } catch (err) {
      toast.error(err.message || "Errore nel caricamento dati iniziali");
      setGiocatori([]);
      setStagioni([]);
      setArticoli([]);
    } finally {
      setLoadingLookup(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchLookup();
  }, [fetchLookup]);

  const articoliSelezionatiIds = useMemo(
    () => righe.map((r) => r.articoloOption?.value).filter(Boolean),
    [righe]
  );

  const righeConTotali = useMemo(() => {
    return righe.map((riga) => {
      const articolo = riga.articoloOption?.raw || null;
      const prezzoDefault = articolo?.prezzoVendita ?? 0;
      const prezzo = toNumberOrNull(riga.prezzoUnitario);
      const prezzoEffettivo = prezzo === null ? Number(prezzoDefault || 0) : prezzo;
      const quantita = Number(riga.quantita || 0);
      const totaleRiga = quantita > 0 ? prezzoEffettivo * quantita : 0;

      return {
        ...riga,
        prezzoEffettivo,
        totaleRiga
      };
    });
  }, [righe]);

  const totaleDovuto = useMemo(
    () => righeConTotali.reduce((acc, r) => acc + Number(r.totaleRiga || 0), 0),
    [righeConTotali]
  );

  const pagatoInizialeNumero = useMemo(
    () => Math.max(0, Number(toNumberOrNull(importoPagatoIniziale) || 0)),
    [importoPagatoIniziale]
  );

  const residuo = useMemo(
    () => Math.max(0, totaleDovuto - pagatoInizialeNumero),
    [totaleDovuto, pagatoInizialeNumero]
  );

  const giocatoreSelezionato = giocatoreOption?.raw || null;

  const addRiga = () => {
    setRighe((prev) => [...prev, createEmptyRiga()]);
  };

  const removeRiga = (key) => {
    setRighe((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((r) => r.key !== key);
    });
  };

  const updateRiga = (key, patch) => {
    setRighe((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        return { ...r, ...patch };
      })
    );
  };

  const onSelectArticolo = (key, opt) => {
    const prezzoVendita = opt?.raw?.prezzoVendita ?? "";
    updateRiga(key, {
      articoloOption: opt || null,
      prezzoUnitario: prezzoVendita === null || prezzoVendita === undefined ? "" : String(prezzoVendita)
    });
  };

  const validaForm = () => {
    if (!giocatoreOption) {
      toast.error("Seleziona un giocatore");
      return false;
    }
    if (!stagioneOption) {
      toast.error("Seleziona una stagione");
      return false;
    }
    if (!righe.length) {
      toast.error("Inserisci almeno un articolo");
      return false;
    }

    for (const riga of righeConTotali) {
      if (!riga.articoloOption) {
        toast.error("Ogni riga deve avere un articolo selezionato");
        return false;
      }
      if (!riga.quantita || Number(riga.quantita) <= 0) {
        toast.error("Inserisci una quantità valida per ogni articolo");
        return false;
      }
      if (riga.prezzoEffettivo < 0) {
        toast.error("Prezzo unitario non valido");
        return false;
      }

      const disp = Number(riga.articoloOption?.raw?.quantitaDisponibile || 0);
      if (Number(riga.quantita) > disp) {
        toast.error(`Quantità non disponibile per ${riga.articoloOption?.raw?.nome || "l'articolo selezionato"}`);
        return false;
      }
    }

    if (pagatoInizialeNumero > totaleDovuto) {
      toast.error("L'importo pagato iniziale non può superare il totale dovuto");
      return false;
    }

    if (pagatoInizialeNumero > 0 && !metodoPagamentoOption) {
      toast.error("Seleziona il metodo di pagamento");
      return false;
    }

    return true;
  };

  const buildPayload = () => ({
    idGiocatore: giocatoreOption.value,
    idGenitore:
      giocatoreSelezionato?.idGenitore ||
      giocatoreSelezionato?.genitore?.id ||
      null,
    idStagione: stagioneOption.value,
    importoPagatoIniziale: pagatoInizialeNumero > 0 ? pagatoInizialeNumero : 0,
    metodoPagamento: pagatoInizialeNumero > 0 ? metodoPagamentoOption?.value || null : null,
    note: note.trim() || null,
    righe: righeConTotali.map((riga) => ({
      idArticolo: riga.articoloOption.value,
      quantita: Number(riga.quantita),
      prezzoUnitario: Number(riga.prezzoEffettivo)
    }))
  });

  const resetForm = () => {
    setGiocatoreOption(null);
    setStagioneOption(null);
    setMetodoPagamentoOption(null);
    setImportoPagatoIniziale("");
    setNote("");
    setRighe([createEmptyRiga()]);
  };

  const onSubmit = async (e) => {
  e.preventDefault();
  if (!validaForm()) return;

  setSaving(true);
  try {
    const payload = buildPayload();

    const res = await authFetch(`${API_URL}/api/admin/acquisti-materiale`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || "Errore durante il salvataggio dell'acquisto materiale");
    }

    

    toast.success("Acquisto materiale registrato correttamente");
    resetForm();
    await fetchLookup();

    
  } catch (err) {
    toast.error(err.message || "Errore durante il salvataggio");
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="sfondo-immagine">
      <div className="acquisto-materiale-page">
        <div className="acquisto-materiale-container">
          <div className="acquisto-materiale-header card-glass">
            <div className="acquisto-materiale-header-top">
              <div>
                <h2 className="acquisto-materiale-title">Registrazione Acquisto Materiale</h2>
                <p className="acquisto-materiale-subtitle">
                  Registra la vendita del materiale sportivo, aggiorna il magazzino e collega l’incasso alla cassa.
                </p>
              </div>

              <button
                type="button"
                className="btn-secondario"
                onClick={() => navigate("/gestionale")}
              >
                Torna al gestionale
              </button>
            </div>
          </div>

          <form className="acquisto-materiale-grid" onSubmit={onSubmit}>
            <div className="card-glass acquisto-card">
              <h3>Dati acquisto</h3>

              <div className="acquisto-fields two-columns">
                <div className="acquisto-field">
                  <label>Giocatore</label>
                  <Select
                    {...selectPortalProps}
                    options={giocatori}
                    value={giocatoreOption}
                    onChange={setGiocatoreOption}
                    placeholder={loadingLookup ? "Caricamento giocatori..." : "Seleziona giocatore"}
                    styles={selectStyles}
                    isClearable
                    isDisabled={loadingLookup}
                  />
                </div>

                <div className="acquisto-field">
                  <label>Stagione</label>
                  <Select
                    {...selectPortalProps}
                    options={stagioni}
                    value={stagioneOption}
                    onChange={setStagioneOption}
                    placeholder={loadingLookup ? "Caricamento stagioni..." : "Seleziona stagione"}
                    styles={selectStyles}
                    isClearable
                    isDisabled={loadingLookup}
                  />
                </div>

                <div className="acquisto-field acquisto-field-full">
                  <label>Note</label>
                  <textarea
                    rows="3"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note acquisto"
                  />
                </div>
              </div>
            </div>

            <div className="card-glass acquisto-card">
              <div className="acquisto-card-title-row">
                <h3>Articoli acquistati</h3>
                <button
                  type="button"
                  className="btn-secondario"
                  onClick={addRiga}
                >
                  Aggiungi articolo
                </button>
              </div>

              <div className="righe-acquisto-wrapper">
                {righeConTotali.map((riga, index) => {
                  const disponibili = articoli.filter((opt) => {
                    const id = opt.value;
                    return !articoliSelezionatiIds.includes(id) || id === riga.articoloOption?.value;
                  });

                  return (
                    <div key={riga.key} className="riga-acquisto-card">
                      <div className="riga-acquisto-top">
                        <h4>Riga {index + 1}</h4>
                        {righe.length > 1 && (
                          <button
                            type="button"
                            className="btn-rimuovi-riga"
                            onClick={() => removeRiga(riga.key)}
                          >
                            Rimuovi
                          </button>
                        )}
                      </div>

                      <div className="acquisto-fields four-columns">
                        <div className="acquisto-field field-span-2">
                          <label>Articolo</label>
                          <Select
                            {...selectPortalProps}
                            options={disponibili}
                            value={riga.articoloOption}
                            onChange={(opt) => onSelectArticolo(riga.key, opt)}
                            placeholder="Seleziona articolo"
                            styles={selectStyles}
                            isClearable
                            isDisabled={loadingLookup}
                          />
                        </div>

                        <div className="acquisto-field">
                          <label>Quantità</label>
                          <input
                            type="number"
                            min="1"
                            value={riga.quantita}
                            onChange={(e) =>
                              updateRiga(riga.key, { quantita: e.target.value })
                            }
                          />
                        </div>

                        <div className="acquisto-field">
                          <label>Prezzo unitario</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={riga.prezzoUnitario}
                            onChange={(e) =>
                              updateRiga(riga.key, { prezzoUnitario: e.target.value })
                            }
                          />
                        </div>
                      </div>

                      <div className="riga-info-inline">
                        <span>
                          Disponibilità:{" "}
                          <strong>{riga.articoloOption?.raw?.quantitaDisponibile ?? "-"}</strong>
                        </span>
                        <span>
                          Taglia: <strong>{riga.articoloOption?.raw?.taglia || "-"}</strong>
                        </span>
                        <span>
                          Totale riga: <strong>{formatEuro(riga.totaleRiga)} €</strong>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card-glass acquisto-card">
              <h3>Pagamento iniziale</h3>

              <div className="acquisto-fields two-columns">
                <div className="acquisto-field">
                  <label>Importo pagato iniziale</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={importoPagatoIniziale}
                    onChange={(e) => setImportoPagatoIniziale(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="acquisto-field">
                  <label>Metodo pagamento</label>
                  <Select
                    {...selectPortalProps}
                    options={metodoPagamentoOptions}
                    value={metodoPagamentoOption}
                    onChange={setMetodoPagamentoOption}
                    placeholder="Seleziona metodo"
                    styles={selectStyles}
                    isClearable
                    isDisabled={pagatoInizialeNumero <= 0}
                  />
                </div>
              </div>
            </div>

            <div className="card-glass acquisto-card acquisto-riepilogo-card">
              <h3>Riepilogo economico</h3>

              <div className="acquisto-riepilogo-grid">
                <div>
                  <span>Giocatore</span>
                  <strong>{giocatoreOption?.label || "-"}</strong>
                </div>
                <div>
                  <span>Stagione</span>
                  <strong>{stagioneOption?.label || "-"}</strong>
                </div>
                <div>
                  <span>Totale dovuto</span>
                  <strong>{formatEuro(totaleDovuto)} €</strong>
                </div>
                <div>
                  <span>Pagato iniziale</span>
                  <strong>{formatEuro(pagatoInizialeNumero)} €</strong>
                </div>
                <div>
                  <span>Residuo</span>
                  <strong>{formatEuro(residuo)} €</strong>
                </div>
                <div>
                  <span>Numero articoli</span>
                  <strong>{righeConTotali.reduce((acc, r) => acc + Number(r.quantita || 0), 0)}</strong>
                </div>
              </div>

              <div className="acquisto-actions">
                <button
                  type="button"
                  className="btn-secondario"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Pulisci
                </button>

                <button
                  type="submit"
                  className="btn-primario"
                  disabled={saving || loadingLookup}
                >
                  {saving ? "Salvataggio..." : "Salva acquisto materiale"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}